// web/src/scene/rtls/useHistoryBounds.ts
//
// Derives the time window used by the playback scrubber + auto-play advancer.
// Scoped to the currently-selected asset's trail when present, so the
// scrubber spans exactly the segment the user sees animating. Falls back to
// the full fleet window when nothing is selected (or the selected asset has
// no trail) so a user who enters playback without a pre-selection still gets
// a usable range.

import { useMemo } from 'react'
import { useAppSelector } from '../../app/useAppSelector'

interface HistoryBounds {
  minTs: number
  maxTs: number
  /** True when bounds were derived from the selected asset's trail only. */
  scopedToAsset: boolean
}

export function useHistoryBounds(): HistoryBounds {
  const positionHistory = useAppSelector(s => s.rtls.positionHistory)
  const selectedAssetId = useAppSelector(s => s.rtls.selectedAssetId)

  return useMemo(() => {
    const now = Date.now()

    // Prefer the selected asset's own trail window.
    if (selectedAssetId) {
      const entries = positionHistory[selectedAssetId]
      if (entries && entries.length > 0) {
        let min = Infinity
        let max = -Infinity
        for (const e of entries) {
          if (e.timestamp < min) min = e.timestamp
          if (e.timestamp > max) max = e.timestamp
        }
        if (Number.isFinite(min) && Number.isFinite(max)) {
          return { minTs: min, maxTs: max, scopedToAsset: true }
        }
      }
    }

    // Fallback: min/max across every asset's trail.
    let min = Infinity
    let max = -Infinity
    for (const entries of Object.values(positionHistory)) {
      for (const e of entries) {
        if (e.timestamp < min) min = e.timestamp
        if (e.timestamp > max) max = e.timestamp
      }
    }
    return {
      minTs: Number.isFinite(min) ? min : now,
      maxTs: Number.isFinite(max) ? max : now,
      scopedToAsset: false,
    }
  }, [positionHistory, selectedAssetId])
}
